import type { PlaceholderRef, PlaceholderType, Transform } from '@slideforge/shared';
import type { Element } from './xml.js';
import { parseTransform } from './parse/geometry.js';

/**
 * The placeholder inheritance chain.
 *
 * A shape on a slide inherits position, size and text formatting from its
 * matching placeholder on the layout, which in turn inherits from the master,
 * which falls back to the theme. This is the single largest source of subtle
 * layout bugs in an OOXML renderer, so the matching rules live in one class with
 * their own tests rather than being re-derived at each call site.
 */

export interface PlaceholderSource {
  readonly type: PlaceholderType | undefined;
  readonly idx: number | undefined;
  readonly transform: Transform | undefined;
  readonly spPr: Element | undefined;
  readonly bodyPr: Element | undefined;
  readonly listStyle: Element | undefined;
}

/** Placeholder types PowerPoint treats as interchangeable when matching by type. */
const FAMILIES: readonly (readonly PlaceholderType[])[] = [
  ['title', 'ctrTitle'],
  ['body', 'subTitle', 'obj'],
  ['dt'],
  ['ftr'],
  ['sldNum'],
];

function familyOf(type: PlaceholderType | undefined): readonly PlaceholderType[] | undefined {
  if (!type) return undefined;
  return FAMILIES.find((f) => f.includes(type));
}

export function collectPlaceholders(spTree: Element | undefined): PlaceholderSource[] {
  if (!spTree) return [];
  const out: PlaceholderSource[] = [];
  for (const sp of spTree.walk()) {
    if (sp.name !== 'p:sp') continue;
    const nvPr = sp.path('p:nvSpPr', 'p:nvPr');
    const ph = nvPr?.child('p:ph');
    if (!ph) continue;
    const idxRaw = ph.attr('idx');
    out.push({
      type: (ph.attr('type') as PlaceholderType | undefined) ?? 'obj',
      idx: idxRaw === undefined ? undefined : Number.parseInt(idxRaw, 10),
      transform: parseTransform(sp.path('p:spPr', 'a:xfrm')),
      spPr: sp.child('p:spPr'),
      bodyPr: sp.path('p:txBody', 'a:bodyPr'),
      listStyle: sp.path('p:txBody', 'a:lstStyle'),
    });
  }
  return out;
}

export class PropertyResolver {
  constructor(
    private readonly layout: readonly PlaceholderSource[],
    private readonly master: readonly PlaceholderSource[],
    /** `p:txStyles` from the master, used for level-based text defaults. */
    private readonly masterTextStyles: {
      readonly title: Element | undefined;
      readonly body: Element | undefined;
      readonly other: Element | undefined;
    },
  ) {}

  /**
   * Matches a slide placeholder to its layout counterpart, then the master's.
   *
   * `idx` is what PowerPoint actually matches on; type is the fallback, and the
   * only thing available for title/body/footer placeholders that carry no idx.
   */
  match(ph: PlaceholderRef): { layout?: PlaceholderSource; master?: PlaceholderSource } {
    const layout = findMatch(this.layout, ph);
    // The master is matched from the layout placeholder's own identity when we
    // found one — a layout can renumber idx values relative to the master.
    const masterRef: PlaceholderRef = layout
      ? { type: layout.type ?? ph.type, ...(layout.idx !== undefined ? { idx: layout.idx } : {}) }
      : ph;
    const master = findMatch(this.master, masterRef);
    return {
      ...(layout ? { layout } : {}),
      ...(master ? { master } : {}),
    };
  }

  /** The first transform found walking shape -> layout -> master. */
  resolveTransform(ph: PlaceholderRef, own: Transform | undefined): {
    transform: Transform | undefined;
    inherited: boolean;
  } {
    if (own && (own.cx !== 0 || own.cy !== 0)) return { transform: own, inherited: false };
    const { layout, master } = this.match(ph);
    const inheritedTransform = layout?.transform ?? master?.transform;
    if (inheritedTransform) return { transform: inheritedTransform, inherited: true };
    return { transform: own, inherited: false };
  }

  /**
   * The `a:lstStyle` elements that apply to a placeholder, ordered from least to
   * most specific so a caller can merge them in one pass.
   */
  resolveListStyles(ph: PlaceholderRef): Element[] {
    const { layout, master } = this.match(ph);
    const styles: Element[] = [];
    const masterStyle = this.masterTextStyleFor(ph.type);
    if (masterStyle) styles.push(masterStyle);
    if (master?.listStyle) styles.push(master.listStyle);
    if (layout?.listStyle) styles.push(layout.listStyle);
    return styles;
  }

  resolveBodyProperties(ph: PlaceholderRef): Element | undefined {
    const { layout, master } = this.match(ph);
    return layout?.bodyPr ?? master?.bodyPr;
  }

  private masterTextStyleFor(type: PlaceholderType): Element | undefined {
    const family = familyOf(type);
    if (family?.includes('title')) return this.masterTextStyles.title;
    if (family?.includes('body')) return this.masterTextStyles.body;
    return this.masterTextStyles.other;
  }
}

function findMatch(
  candidates: readonly PlaceholderSource[],
  ph: PlaceholderRef,
): PlaceholderSource | undefined {
  if (ph.idx !== undefined) {
    const byIdx = candidates.find((c) => c.idx === ph.idx);
    if (byIdx) return byIdx;
  }

  const family = familyOf(ph.type);
  if (family) {
    const byFamily = candidates.find((c) => c.type !== undefined && family.includes(c.type));
    if (byFamily) return byFamily;
  }

  const byType = candidates.find((c) => c.type === ph.type);
  if (byType) return byType;

  // A placeholder with neither idx nor a matching type falls back to the first
  // candidate with no idx of its own, which is how bare `<p:ph/>` behaves.
  if (ph.idx === undefined) {
    return candidates.find((c) => c.idx === undefined);
  }
  return undefined;
}

/** Reads the level-indexed defaults out of an `a:lstStyle` or `p:titleStyle`. */
export function levelStyle(listStyle: Element | undefined, level: number): Element | undefined {
  if (!listStyle) return undefined;
  const clamped = Math.max(0, Math.min(8, level));
  return listStyle.child(`a:lvl${clamped + 1}pPr`);
}
